import {
  ViajesFirmaRequest,
  ViajesFirmaResponse,
  ViajesFirmaTipo,
} from 'src/core/apis/viajes.firma';

export type { ViajesFirmaTipo };

export const VIAJES_FIRMA_EMAIL_PATTERN =
  /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

export interface ViajesFirmaPayloadInput {
  tkt: string;
  orderNumber: string;
  rut: string;
  name: string;
  signature: string;
  email: string;
  /** Segundo correo. Solo se envía cuando el caller lo define (áridos). */
  emailDos?: string;
  tipoFirma?: ViajesFirmaTipo;
  comentario?: string;
}

export function isCommentRequiredFirmaTipo(tipo?: ViajesFirmaTipo): boolean {
  return tipo === 'ADICION_AGUA' || tipo === 'DEVOLUCION_HORMIGON';
}

const VIAJES_FIRMA_MAX_TKT_LENGTH = 10;
const VIAJES_FIRMA_MAX_ORDER_NUMBER_LENGTH = 20;
const VIAJES_FIRMA_MAX_SIGNATURE_BYTES = 5 * 1024 * 1024;
const VIAJES_FIRMA_PNG_DATA_URI_PREFIX = 'data:image/png;base64,';
const VIAJES_FIRMA_PNG_HEADER = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

export function assertViajesFirmaPayload(input: ViajesFirmaPayloadInput): void {
  const { tkt, orderNumber, rut, name, signature, email } = input;
  const normalizedTkt = (tkt ?? '').trim();
  const normalizedOrderNumber = (orderNumber ?? '').trim();

  if (!normalizedTkt || !normalizedOrderNumber) {
    throw new Error('tkt y orderNumber son obligatorios para /viajes/firma');
  }
  if (normalizedTkt.length > VIAJES_FIRMA_MAX_TKT_LENGTH) {
    throw new Error('El ticket no puede superar los 10 caracteres.');
  }
  if (normalizedOrderNumber.length > VIAJES_FIRMA_MAX_ORDER_NUMBER_LENGTH) {
    throw new Error('El numero de pedido no puede superar los 20 caracteres.');
  }
  if (!name?.trim()) {
    throw new Error('El nombre del firmante es obligatorio.');
  }
  const rutLen = (rut ?? '').trim().length;
  if (rutLen < 8 || rutLen > 9) {
    throw new Error('El RUT del firmante debe tener 8 o 9 caracteres.');
  }
  if (/[kK]{2}/.test(rut)) {
    throw new Error('El RUT del firmante no es valido.');
  }
  if (!signature?.trim()) {
    throw new Error('La firma es obligatoria.');
  }
  assertPngSignature(signature);
  const mail = (email ?? '').trim();
  if (mail && !VIAJES_FIRMA_EMAIL_PATTERN.test(mail)) {
    throw new Error('El correo ingresado no es valido.');
  }
  if (input.emailDos !== undefined) {
    const mailDos = input.emailDos.trim();
    if (mailDos && !VIAJES_FIRMA_EMAIL_PATTERN.test(mailDos)) {
      throw new Error('El correo adicional ingresado no es valido.');
    }
  }
  if (isCommentRequiredFirmaTipo(input.tipoFirma) && !(input.comentario ?? '').trim()) {
    throw new Error('El comentario es obligatorio para este tipo de firma.');
  }
}

export function buildViajesFirmaRequest(
  input: ViajesFirmaPayloadInput
): ViajesFirmaRequest {
  assertViajesFirmaPayload(input);
  const body: ViajesFirmaRequest = {
    mx_tkt_h: input.tkt.trim(),
    mx_numero_pedido_h: input.orderNumber.trim(),
    mx_cli_firma_rut: input.rut.trim(),
    mx_cli_firma_nombre: input.name.trim(),
    mx_cli_firma: input.signature.trim(),
    mx_cli_firma_correo: (input.email ?? '').trim(),
  };
  if (input.emailDos !== undefined) {
    body.mx_cli_firma_correo_dos = input.emailDos.trim();
  }
  if (input.tipoFirma) {
    body.tipo_firma = input.tipoFirma;
  }
  const comentario = (input.comentario ?? '').trim();
  if (comentario) {
    body.comentario = comentario;
  }
  return body;
}

export function isViajesFirmaSuccessResponse(
  raw: unknown
): raw is ViajesFirmaResponse {
  if (!raw || typeof raw !== 'object') {
    return false;
  }
  return (raw as ViajesFirmaResponse).success === true;
}

export function parseViajesFirmaErrorMessage(raw: unknown): string {
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    if (typeof r['message'] === 'string' && r['message'].trim()) {
      return r['message'];
    }
    if (r['errors'] !== undefined && r['errors'] !== null) {
      return String(r['errors']);
    }
  }
  return 'La firma no pudo ser almacenada.';
}

function assertPngSignature(signature: string): void {
  const value = signature.trim();
  let base64Value = value;

  if (value.toLowerCase().startsWith('data:')) {
    if (!value.toLowerCase().startsWith(VIAJES_FIRMA_PNG_DATA_URI_PREFIX)) {
      throw new Error('La firma debe tener formato PNG: data:image/png;base64.');
    }
    base64Value = value.substring(VIAJES_FIRMA_PNG_DATA_URI_PREFIX.length);
  }

  base64Value = base64Value.replace(/\r/g, '').replace(/\n/g, '').replace(/ /g, '');

  if (!base64Value.trim()) {
    throw new Error('La firma Base64 esta vacia.');
  }

  let binary: string;
  try {
    binary = atob(base64Value);
  } catch (_e) {
    throw new Error('El contenido de la firma no es un Base64 valido.');
  }

  if (!binary.length) {
    throw new Error('La firma decodificada esta vacia.');
  }
  if (binary.length > VIAJES_FIRMA_MAX_SIGNATURE_BYTES) {
    throw new Error('La firma supera el tamano maximo permitido de 5 MB.');
  }
  if (binary.length < VIAJES_FIRMA_PNG_HEADER.length) {
    throw new Error('El contenido recibido no corresponde a una imagen PNG.');
  }

  const isPng = VIAJES_FIRMA_PNG_HEADER.every(
    (byte, index) => binary.charCodeAt(index) === byte
  );
  if (!isPng) {
    throw new Error('El contenido Base64 no corresponde a una imagen PNG valida.');
  }
}

const ALREADY_PROCESSED_CODES = new Set([
  'SIGNATURE_ALREADY_EXISTS',
  'ALREADY_SIGNED',
  'SIGNATURE_ALREADY_SAVED',
]);

const ALREADY_PROCESSED_MESSAGE_RE =
  /ya fue firmad|already signed|already processed|ya existe|duplicate/i;

export function isAlreadyProcessedError(
  raw: unknown,
  httpStatus?: number
): boolean {
  if (httpStatus === 409) {
    return true;
  }
  if (!raw || typeof raw !== 'object') {
    if (typeof raw === 'string') {
      return ALREADY_PROCESSED_MESSAGE_RE.test(raw);
    }
    return false;
  }
  const r = raw as Record<string, unknown>;
  const code = String(r['code'] ?? '').toUpperCase();
  if (ALREADY_PROCESSED_CODES.has(code)) {
    return true;
  }
  const message = String(r['message'] ?? '');
  return ALREADY_PROCESSED_MESSAGE_RE.test(message);
}
